import dayjs from "dayjs";
import { clampGapMinutes } from "./durationFormat";

/** Parse segment_gap_overrides from API (object or JSON string). */
export function parseSegmentGapOverrides(raw) {
  if (raw == null) return null;

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }

  const out = {};
  for (const [key, value] of Object.entries(parsed)) {
    const mins = Number(value);
    if (!Number.isFinite(mins) || mins < 0 || mins > 480 || mins % 15 !== 0) {
      continue;
    }
    out[String(key)] = mins;
  }

  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Build service list rows: booked segments with a gap row between
 * consecutive blocks that have space between them.
 */
export function buildSegmentTimeline(segments) {
  const sorted = [...(segments || [])].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const items = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const seg = sorted[i];
    items.push({
      type: "segment",
      key: `segment-${seg.event_id}`,
      segment: seg,
    });

    const next = sorted[i + 1];
    if (!next) continue;

    const gapStart = dayjs(seg?.services?.[0]?.serviceEnd ?? seg.end);
    const gapEnd = dayjs(next?.services?.[0]?.serviceStart ?? next.start);
    const gapMinutes = gapEnd.diff(gapStart, "minute");

    if (!Number.isFinite(gapMinutes) || gapMinutes <= 0) continue;

    const serviceId =
      seg?.services?.[0]?.service_id ??
      next?.services?.[0]?.service_id ??
      null;

    items.push({
      type: "gap",
      key: `gap-${seg.event_id}-${next.event_id}`,
      serviceId,
      beforeSegment: seg,
      afterSegment: next,
      gapMinutes,
      gapStart,
      gapEnd,
      title:
        seg.services?.[0]?.title ??
        next.services?.[0]?.title ??
        seg.title ??
        "Service",
    });
  }

  return items;
}

/** Merge one service gap override into the existing map for PUT. */
export function buildSegmentGapOverridesPayload(
  existingOverrides,
  serviceId,
  gapMinutes
) {
  return {
    ...(existingOverrides ?? {}),
    [String(serviceId)]: clampGapMinutes(gapMinutes),
  };
}
